import { AutoPayFrequency, BalanceDetailsDTO, FeesDTO, PaymentTypeConfigDto, PaymentTypeEnum, ResidentDTO, Role } from "@inosio/payments-sdk";
import { MaskedInputState } from "../../../shared/MaskedInput";
import { useSelector } from "react-redux";
import { selectBalanceDetails, selectPaymentState } from "../sagas/residentpaymentsSlice";
import { usePmProfile } from "../../../shared/login/usePmActor";
import { useContext } from "react";
import { UIContextEnum, defaultUIContext } from "../../../pmui/residents/MakeAdhocOtp";
import { selectResidentBalanceDetails } from "../../../pmui/residents/saga/pmResidentsSlice";
import { format, differenceInDays, endOfToday, subBusinessDays } from "date-fns";
import { selectResidentAutoPayHistory, selectResidentTransactionHistory } from "../../../pmui/residents/saga/pmResidentsSlice";
import { selectAutoPays, selectTransactionHistory } from "../sagas/residentpaymentsSlice";
import { IpaUserExtended, selectCurrentUser } from "../../../shared/sagas/auth/authSlice";

export function useRecentOTPsAndAutoPays(CURRENT_UI_CONTEXT: UIContextEnum) {
  const otpTransactions = useSelector(CURRENT_UI_CONTEXT === UIContextEnum.pmUI ? selectResidentTransactionHistory : selectTransactionHistory);
  const autoPayTransactions = useSelector(CURRENT_UI_CONTEXT === UIContextEnum.pmUI ? selectResidentAutoPayHistory : selectAutoPays) ?? [];

  const pegDate = subBusinessDays(endOfToday(), 3);
  const last3BusinessDaysOtpTrx = otpTransactions?.filter((trans) => {
    const isTrxInLast3BusDays = trans.TransactionDate && new Date(trans.TransactionDate) > pegDate;

    return isTrxInLast3BusDays && (trans.Status === "Pending" || trans.Status === "Completed");
  });
  last3BusinessDaysOtpTrx?.sort((a: any, b: any) => (new Date(a.TransactionDate) > new Date(b.TransactionDate) ? 1 : -1));

  const formattedTodayDate = format(new Date(), "yyyy-MM-dd");
  const threeDaysRecurringPayments = autoPayTransactions?.map((autoPay: any) => {
    const [year, month, day] = autoPay.NextScheduledPaymentDate.split('-');
    const FormattedNSPDate = `${month}/${day}/${year}`;

    if (([0, 1, 2, 3, 4, 5].includes(differenceInDays(new Date(autoPay.NextScheduledPaymentDate), new Date(formattedTodayDate)))) && autoPay.AutoPayResults?.length === 0) {
      return { ...autoPay, Status: 'Upcoming', FormattedNSPDate };
    } else if (autoPay.NextScheduledPaymentDate === formattedTodayDate && autoPay.AutoPayResults?.find((ap: any) => ap.IsSuccessful)) {
      return { ...autoPay, Status: 'Recent', FormattedNSPDate };
    } else {
      return false
    }
  }).filter(Boolean);
  threeDaysRecurringPayments?.sort((a: any, b: any) => (new Date(a.NextScheduledPaymentDate) > new Date(b.NextScheduledPaymentDate) ? 1 : -1));

  return { otps: last3BusinessDaysOtpTrx || [], autopays: threeDaysRecurringPayments || [] };
}

export const createTransDetailRecords = (maskedInputValues: MaskedInputState, currentBalanceInfo: BalanceDetailsDTO, paymentMethodUuid: string) => {


  //temp workaround until we can address the duplicate ppt issue.
  const returnObj2: { Amount: number; PropertyPaymentTypeId: number; FromPayMethodUuid: string; PaymentTypeDesc: string; }[] =  [];

  if(!currentBalanceInfo) return returnObj2;
  const amtOwedPpt = currentBalanceInfo?.PropertyPaymentTypes?.find(ppt=>ppt.Label==='Amount Owed');
  if(amtOwedPpt) returnObj2.push({
    Amount: +Number.parseFloat(maskedInputValues.amountOwed).toFixed(2).replace(".", ""), //temp fix for the rounding error
    PropertyPaymentTypeId: amtOwedPpt.PropertyPaymentTypeID,
    FromPayMethodUuid: paymentMethodUuid,
    PaymentTypeDesc: amtOwedPpt.Label,
  });
  const secDepPpt = currentBalanceInfo?.PropertyPaymentTypes?.find(ppt=>ppt.Label==='Security Deposit');
  if(secDepPpt) returnObj2.push({
    Amount: +Number.parseFloat(maskedInputValues.securityDeposit).toFixed(2).replace(".", ""), //temp fix for the rounding error
    PropertyPaymentTypeId: secDepPpt.PropertyPaymentTypeID,
    FromPayMethodUuid: paymentMethodUuid,
    PaymentTypeDesc: secDepPpt.Label,
  });

  const miscPpt = currentBalanceInfo?.PropertyPaymentTypes?.find(ppt=>ppt.Label==='Misc');

  if(miscPpt) returnObj2.push({
    Amount: +Number.parseFloat(maskedInputValues.misc).toFixed(2).replace(".", ""), //temp fix for the rounding error
    PropertyPaymentTypeId: miscPpt.PropertyPaymentTypeID,
    FromPayMethodUuid: paymentMethodUuid,
    PaymentTypeDesc: miscPpt.Label,
  });
  
  /*more apprioriate way
  const returnObj = (
    
    currentBalanceInfo &&
    currentBalanceInfo.PropertyPaymentTypes.map((paymentType, index) => {
      let amountAsString = '';
      switch (paymentType.Label) {
        case 'Amount Owed':
          amountAsString = maskedInputValues.amountOwed;
          break;
        case 'Security Deposit':
          amountAsString = maskedInputValues.securityDeposit;
          break;
        case 'Misc':
          amountAsString = maskedInputValues.misc;
          break;
      }

      return {
      Amount: +Number.parseFloat(amountAsString).toFixed(2).replace(".", ""), //temp fix for the rounding error
      PropertyPaymentTypeId: paymentType.PropertyPaymentTypeID,
      FromPayMethodUuid: paymentMethodUuid,
      PaymentTypeDesc: paymentType.Label,
    }}
    ).filter((paymentType) => +paymentType.Amount > 0)
  );*/

   const transDetails = returnObj2.filter((paymentType) => +paymentType.Amount > 0);
   return transDetails;
};

export function useSelectedPptId() {
  const currentUI = useContext(defaultUIContext);
  const isInsidePmUI = currentUI === UIContextEnum.pmUI;
  const currentBalanceInfo = useSelector(isInsidePmUI ? selectResidentBalanceDetails: selectBalanceDetails);

  let ppt = currentBalanceInfo.PropertyPaymentTypes?.find((ppt) => ppt.Amount === currentBalanceInfo.Balance);
  ppt = ppt || currentBalanceInfo.PropertyPaymentTypes?.find((ppt) => ppt.Amount > 0); //default to the first positive balance if we can't find one
  ppt = ppt || currentBalanceInfo.PropertyPaymentTypes?.[0]; //default to the first positive balance if we can't find one
  return ppt?.PropertyPaymentTypeID;
}

export function useIsPmAdhoc() {
  const [isPmProfile] = usePmProfile();
  const currentUI = useContext(defaultUIContext);
  const isInsidePmUI = currentUI === UIContextEnum.pmUI;
  return isInsidePmUI && isPmProfile;
}

export const getSumOfPropPayTypesAmount = (maskedInputValues: MaskedInputState) => {
  return fixedStringToMinorUnits(maskedInputValues.amountOwed) +fixedStringToMinorUnits(maskedInputValues.securityDeposit) + fixedStringToMinorUnits(maskedInputValues.misc);
}

/**
 * 
 * @param input "547.28"
 * @returns 54728
 */
export const fixedStringToMinorUnits = (input: string)=>{
  if (!input) return 0;

  //step 1, remove decimal
  let minorUnitsString = input.replace(".", '');

  //step 2, parse int
  let minorUnitsInt = parseInt(minorUnitsString);
  
  return minorUnitsInt;
}

/**
 * 
 * @param input 54728
 * @returns "547.28"
 */
export const minorUnitsToFixedString = (input: number) =>{

  let answer: string = "0";
  
  if (!input) {
    return answer;
  }

  const stringVersion = input.toString();

  if (stringVersion.length < 3) {
    answer = input.toFixed(2);
  } else {
    answer = stringVersion.substring(0,stringVersion.length-2)+"."+stringVersion.substring(stringVersion.length-2);
  }

  return answer;
}
  // const frequency = isMonthly ? "month" : isQuarterly ? "3 months" : isSemiAnnually ? "6 months" : "year";

export const getFrequency = (frequency: AutoPayFrequency) => {
  switch (frequency) {
    case AutoPayFrequency.M:
      return "month";
    case AutoPayFrequency.Q:
      return "3 months";
    case AutoPayFrequency.S:
      return "6 months";
    case AutoPayFrequency.A:
      return "year";
    default:
      break;
  }
}

export const getAvailablePaymentTypes = (paymentTypeConfigs: PaymentTypeConfigDto[], targetDate: number) => {
  const returnValue = paymentTypeConfigs.filter((p) => isActionAllowedInRange(p.NotBeforeDay, p.NotAfterDay, targetDate)).map((p) => `${p.Type}` as PaymentTypeEnum);

  return returnValue;
}

/**
 *
 * @param notBeforeDay
 * @param notAfterDay
 * @param targetDay the numeric day to figure out if it lays between the two days passed in.
 * @returns boolean. True if the day is allowed, false otherwise.
 */
export const isActionAllowedInRange = (notBeforeDay: number, notAfterDay: number, targetDay: number) => {
  //this tells us how to view the winda.
  //if NBF is smaller (ie, [1, 5]), then we check if the target date is "inside the bookends"
  //IF NBF is not smaller (ie, [25, 10]), then we check the target date is "outside of the bookends"

  //scenario 1, NBF and NAF are the same day
  if (notBeforeDay === notAfterDay) {
    return targetDay === notBeforeDay;
  }

  const isNbfIsSmaller = notBeforeDay < notAfterDay;

  if (isNbfIsSmaller) {
    //scenario 2 NBF is smaller than NAF (ie, [1, 5]), so we check if the target day is between these two numbers, inclusively
    return targetDay >= notBeforeDay && targetDay <= notAfterDay;
  } else {
    //scenario 3 NBF is bigger than NAF (ie, [25, 10]),so we check target day is "less" the NBF and GTE the NAF
    return (targetDay >= notBeforeDay && targetDay >= notAfterDay) || (targetDay <= notBeforeDay && targetDay <= notAfterDay);
  }
}

export const getAvailablePaymentMethods = (targetDate: number, paymentTypeConfigs?: PaymentTypeConfigDto[], paymentFeesByResident?: FeesDTO[], residentsDetails?: ResidentDTO) => {
  //get avail payment methods at pm's level
  let returnMethods = paymentTypeConfigs ? getAvailablePaymentTypes(paymentTypeConfigs ?? [], targetDate) : paymentFeesByResident?.map((payment) => payment?.PaymentType) ?? [];

  // check the resident level settings
  if (residentsDetails?.Uuid) {
    if (!residentsDetails.AchEnabled) returnMethods = returnMethods.filter((m) => m !== PaymentTypeEnum.Ach);
    if (!residentsDetails.CreditcardEnabled) returnMethods = returnMethods.filter((m) => m !== PaymentTypeEnum.Cc);
    if (!residentsDetails.DebitcardEnabled) returnMethods = returnMethods.filter((m) => m !== PaymentTypeEnum.Db);
  }

  return returnMethods;
}

export function useCheckAPPromotionStatus() {
  const currentUser = useSelector(selectCurrentUser) as IpaUserExtended;
  const { paymentConfigs, AutoPays } = useSelector(selectPaymentState);

  //step 1: check if currentUser is a resident.
  const { currentProfile } = currentUser;
  if (currentProfile.role !== Role.Resident) return false;

  //step 2: Do they have a recurring payment?
  if (AutoPays && AutoPays.length > 0) return false;

  //step 3: Are recurring payments enabled?
  if (!paymentConfigs?.AutoPayConfigs?.find((ap) => ap.Enabled)) return false;

  return true;
}
